/**
 * Auth Redux Slice
 *
 * Manages authentication state including user info, token, and loading states.
 * Includes persistence to localStorage for token and user data.
 * Integrates with RTK Query authApi for login/logout.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, TokensRefreshedPayload, UserInfo } from '../types/auth.types';
import { authApi } from '../api/authApi';

/**
 * Session persisted by another tab, copied into the store 1:1 (see tokensAdopted)
 */
export interface TokensAdoptedPayload {
  accessToken: string;
  refreshToken: string;
  user: UserInfo;
  tokenExpiresAt: number; // Unix timestamp (ms), exactly as persisted
  passwordChangeRequired: boolean;
}

/**
 * Initial auth state: unauthenticated, no user, no token
 */
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  loading: false,
  error: null,
  tokenExpiresAt: null,
  passwordChangeRequired: false,
  sessionRefreshFailed: false,
};

const parseStoredUser = (userJson: string | null): UserInfo | null => {
  try {
    return userJson ? (JSON.parse(userJson) as UserInfo | null) : null;
  } catch {
    return null;
  }
};

/**
 * Helper: Load persisted auth state from localStorage
 * - unexpired access token with user and expiry: authenticated at once
 * - expired or missing access token but a refresh token: restoring state (sessionRestoring);
 *   only authToken is removed from storage (readers outside the store must never send an
 *   expired token), the rest stays so a failed restore can be retried on the next load
 * - otherwise: everything is cleared
 */
const loadPersistedAuth = (): AuthState => {
  try {
    const token = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('authRefreshToken');
    const userJson = localStorage.getItem('authUser');
    const expiresAtStr = localStorage.getItem('authTokenExpiresAt');
    const passwordChangeRequired = localStorage.getItem('authPasswordChangeRequired') === 'true';
    const user = parseStoredUser(userJson);
    const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : NaN;

    // Token is still valid if expiresAt > now
    if (token && user && expiresAt > Date.now()) {
      return {
        isAuthenticated: true,
        user,
        token,
        refreshToken: refreshToken || null,
        loading: false,
        error: null,
        tokenExpiresAt: expiresAt,
        passwordChangeRequired,
        sessionRefreshFailed: false,
      };
    }

    if (refreshToken) {
      localStorage.removeItem('authToken');
      return {
        ...initialState,
        refreshToken,
        user,
        passwordChangeRequired,
        sessionRestoring: true,
      };
    }

    // No way to renew the session; clear everything
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRefreshToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('authTokenExpiresAt');
    localStorage.removeItem('authPasswordChangeRequired');
  } catch (err) {
    console.error('Failed to load persisted auth state:', err);
  }
  return initialState;
};

const authSlice = createSlice({
  name: 'auth',
  initialState: loadPersistedAuth(),
  reducers: {
    /**
     * Manually set user (e.g., from persisted state)
     */
    setUser: (state, action: PayloadAction<UserInfo | null>) => {
      state.user = action.payload;
      if (action.payload) {
        localStorage.setItem('authUser', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('authUser');
      }
    },

    /**
     * Manually set token
     */
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
      if (action.payload) {
        localStorage.setItem('authToken', action.payload);
      } else {
        localStorage.removeItem('authToken');
      }
    },

    /**
     * Logout: clear all auth data
     */
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.loading = false;
      state.error = null;
      state.tokenExpiresAt = null;
      state.passwordChangeRequired = false;
      state.sessionRefreshFailed = false;
      state.sessionRestoring = false;

      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('authRefreshToken');
      localStorage.removeItem('authUser');
      localStorage.removeItem('authTokenExpiresAt');
      localStorage.removeItem('authPasswordChangeRequired');
    },

    /**
     * Apply a successful silent refresh: new tokens, user and expiry replace the stale ones
     * and are persisted under the same keys as login.
     */
    tokensRefreshed: (state, action: PayloadAction<TokensRefreshedPayload>) => {
      const { accessToken, refreshToken, user, expiresIn, passwordChangeRequired } = action.payload;
      const expiresAt = Date.now() + expiresIn * 1000;
      const nextRefreshToken = refreshToken || state.refreshToken;
      const nextPasswordChangeRequired = passwordChangeRequired ?? state.passwordChangeRequired;

      state.isAuthenticated = true;
      state.token = accessToken;
      state.refreshToken = nextRefreshToken;
      state.user = user;
      state.tokenExpiresAt = expiresAt;
      state.passwordChangeRequired = nextPasswordChangeRequired;
      state.sessionRefreshFailed = false;
      state.sessionRestoring = false;

      localStorage.setItem('authToken', accessToken);
      if (nextRefreshToken) {
        localStorage.setItem('authRefreshToken', nextRefreshToken);
      }
      localStorage.setItem('authUser', JSON.stringify(user));
      localStorage.setItem('authTokenExpiresAt', expiresAt.toString());
      if (nextPasswordChangeRequired) {
        localStorage.setItem('authPasswordChangeRequired', 'true');
      } else {
        localStorage.removeItem('authPasswordChangeRequired');
      }
    },

    /**
     * Adopt a session that another tab already refreshed and persisted. Copies the persisted
     * values 1:1 and writes nothing to localStorage: the other tab owns the storage, and a write
     * (or a recomputed expiry) would raise storage events back in that tab. Never resurrects a
     * logged-out store and ignores a different user. While the session is being restored it
     * completes the restore: the store has no tokens yet and may not know the user.
     */
    tokensAdopted: (state, action: PayloadAction<TokensAdoptedPayload>) => {
      const { accessToken, refreshToken, user, tokenExpiresAt, passwordChangeRequired } =
        action.payload;
      const restoring = state.sessionRestoring === true;
      const sameUser = state.user ? state.user.id === user.id : restoring;
      if ((!state.isAuthenticated && !restoring) || !sameUser) {
        return;
      }

      state.isAuthenticated = true;
      state.sessionRestoring = false;
      state.token = accessToken;
      state.refreshToken = refreshToken;
      state.user = user;
      state.tokenExpiresAt = tokenExpiresAt;
      state.passwordChangeRequired = passwordChangeRequired;
      state.sessionRefreshFailed = false;
    },

    /**
     * Another tab ended the session (logout, storage cleared, different user signed in).
     * Resets the store like logout but never touches localStorage: the other tab owns it, and
     * removing the keys would wipe a session that tab has just stored.
     */
    sessionEndedElsewhere: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.loading = false;
      state.error = null;
      state.tokenExpiresAt = null;
      state.passwordChangeRequired = false;
      state.sessionRefreshFailed = false;
      state.sessionRestoring = false;
    },

    /**
     * The restoring refresh ended without a verdict on the session (network error, 5xx or the
     * session changed meanwhile). The store stays unauthenticated, but localStorage is left
     * alone so the next page load retries the restore.
     */
    sessionRestoreEnded: (state) => {
      state.sessionRestoring = false;
    },

    /**
     * Mark whether the last silent refresh failed with a non-401 error (session is kept)
     */
    setSessionRefreshFailed: (state, action: PayloadAction<boolean>) => {
      state.sessionRefreshFailed = action.payload;
    },

    /**
     * Clear password change required flag
     * Called after successful password change
     */
    clearPasswordChangeRequired: (state) => {
      state.passwordChangeRequired = false;
      localStorage.removeItem('authPasswordChangeRequired');
    },

    /**
     * Clear auth error
     */
    clearError: (state) => {
      state.error = null;
    },
  },

  /**
   * Handle RTK Query login mutation lifecycle
   */
  extraReducers: (builder) => {
    builder
      // Login pending
      .addMatcher(authApi.endpoints.login.matchPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Login fulfilled
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        const { accessToken, refreshToken, user, expiresIn, passwordChangeRequired } =
          action.payload;
        const expiresAt = Date.now() + expiresIn * 1000; // Convert to ms

        state.isAuthenticated = true;
        state.token = accessToken;
        state.refreshToken = refreshToken || null;
        state.user = user;
        state.loading = false;
        state.error = null;
        state.tokenExpiresAt = expiresAt;
        state.passwordChangeRequired = passwordChangeRequired ?? false;
        state.sessionRefreshFailed = false;
        state.sessionRestoring = false;

        // Persist to localStorage
        localStorage.setItem('authToken', accessToken);
        if (refreshToken) {
          localStorage.setItem('authRefreshToken', refreshToken);
        }
        localStorage.setItem('authUser', JSON.stringify(user));
        localStorage.setItem('authTokenExpiresAt', expiresAt.toString());
        if (passwordChangeRequired) {
          localStorage.setItem('authPasswordChangeRequired', 'true');
        } else {
          localStorage.removeItem('authPasswordChangeRequired');
        }
      })
      // Login rejected
      .addMatcher(authApi.endpoints.login.matchRejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.tokenExpiresAt = null;
        state.passwordChangeRequired = false;
        state.sessionRestoring = false;

        // Extract error message
        const errorPayload = action.payload as any;
        state.error =
          errorPayload?.data?.message ||
          errorPayload?.data?.error ||
          'Login failed. Please try again.';

        // Clear localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('authRefreshToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('authTokenExpiresAt');
        localStorage.removeItem('authPasswordChangeRequired');
      });
  },
});

export const {
  setUser,
  setToken,
  logout,
  tokensRefreshed,
  tokensAdopted,
  sessionEndedElsewhere,
  sessionRestoreEnded,
  setSessionRefreshFailed,
  clearPasswordChangeRequired,
  clearError,
} = authSlice.actions;
export default authSlice.reducer;
